// components/PredictionChart.js - Enhanced Professional Version
// Professional black theme matching MarketChart from TradingCommand
// Optimized for institutional trading platform aesthetics

import { useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { Row, Col, Container } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";

const dateToString = (d) => `${d.getHours()}:${d.getMinutes()}`;

const PredictionChart = ({ data, callbackFromParent }) => {
  const [window, setWindow] = useState(80);
  const [scale, setScale] = useState(5);

  const handleSliderChange = (newValue) => {
    setWindow(newValue);
    callbackFromParent(newValue, scale);
  };

  const handleScaleChange = (newValue) => {
    setScale(newValue);
    callbackFromParent(window, newValue);
  };

  // Prepare data series
  const px11 = data.map((data, i) => ({
    x: dateToString(new Date(data.date)),
    y: isNaN(data.correct_up_pct) ? 0 : data.correct_up_pct,
  }));

  const px21 = data.map((data, i) => ({
    x: dateToString(new Date(data.date)),
    y: isNaN(data.correct_down_pct) ? 0 : data.correct_down_pct,
  }));

  const px31 = data.map((data, i) => ({
    x: dateToString(new Date(data.date)),
    y: isNaN(data.correct_pct) ? 0 : data.correct_pct,
  }));

  // Calculate averages
  var positiveData1 = data.filter(function (obj) {
    return !isNaN(obj.correct_up_pct) && obj.correct_up_pct > 0;
  });
  const x1_average =
    positiveData1.length > 0
      ? positiveData1.reduce((sum, obj) => sum + parseFloat(obj.correct_up_pct), 0) / positiveData1.length
      : 0;

  var positiveData2 = data.filter(function (obj) {
    return !isNaN(obj.correct_down_pct) && obj.correct_down_pct > 0;
  });
  const x2_average =
    positiveData2.length > 0
      ? positiveData2.reduce((sum, obj) => sum + parseFloat(obj.correct_down_pct), 0) / positiveData2.length
      : 0;

  var positiveData3 = data.filter(function (obj) {
    return !isNaN(obj.correct_pct) && obj.correct_pct > 0;
  });
  const x3_average =
    positiveData3.length > 0
      ? positiveData3.reduce((sum, obj) => sum + parseFloat(obj.correct_pct), 0) / positiveData3.length
      : 0;

  // Professional ApexCharts options matching MarketChart theme
  const options = {
    chart: {
      height: 400,
      type: "line",
      background: "#000000",
      foreColor: "#ffffff",
      zoom: {
        enabled: true,
        type: "x",
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true,
        },
      },
    },
    theme: {
      mode: "dark",
    },
    grid: {
      show: true,
      borderColor: "#1a1a1a",
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: 15,
        bottom: 0,
        left: 15
      }
    },
    stroke: {
      curve: "smooth",
      width: [3, 2, 2],
    },
    fill: {
      type: ["gradient", "solid", "solid"],
      gradient: {
        shade: "dark",
        type: "vertical",
        shadeIntensity: 0.4,
        gradientToColors: ["#00C3FF"],
        inverseColors: false,
        opacityFrom: 0.6,
        opacityTo: 0.1,
        stops: [0, 100],
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: [0, 3, 3],
      strokeWidth: 1,
      strokeColors: "#fff",
      hover: {
        size: 5,
      },
    },
    xaxis: {
      type: "category",
      labels: {
        style: {
          colors: "#b0b0b0",
          fontSize: "11px",
        },
        rotate: -45,
        rotateAlways: false,
      },
      axisBorder: {
        show: true,
        color: "#333333",
      },
      axisTicks: {
        show: true,
        color: "#333333",
      },
    },
    yaxis: {
      title: {
        text: "Accuracy (%)",
        style: {
          color: "#b0b0b0",
          fontSize: "12px",
        },
      },
      labels: {
        formatter: (value) => {
          if (value === null || value === undefined) return "";
          return value.toFixed(1) + "%";
        },
        style: {
          colors: "#b0b0b0",
          fontSize: "11px",
        },
      },
      axisBorder: {
        show: true,
        color: "#333333",
      },
      min: 0,
      max: 100,
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      shared: true,
      intersect: false,
      style: {
        fontSize: "12px",
      },
      y: {
        formatter: function (value) {
          if (value === null || value === undefined) return "";
          return value.toFixed(2) + "%";
        },
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "center",
      labels: {
        colors: "#ffffff",
      },
      markers: {
        width: 12,
        height: 12,
      },
    },
  };

  let series = [
    {
      name: "Overall Accuracy",
      type: "area",
      data: px31,
      color: "#00C3FF",
    },
    {
      name: "Long Predictions",
      type: "line",
      data: px11,
      color: "#00b746",
    },
    {
      name: "Short Predictions",
      type: "line",
      data: px21,
      color: "#ef403c",
    },
  ];

  return (
    <div style={{ padding: "0" }}>
      {/* Header Metrics - Professional layout */}
      <Container fluid style={{ padding: "0 0 20px 0" }}>
        <Row className="align-items-center">
          {/* Accuracy Summary */}
          <Col xs={12} lg={6} className="mb-3 mb-lg-0">
            <div
              style={{
                display: "flex",
                gap: "20px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                  Overall
                </div>
                <div style={{ fontSize: "20px", fontWeight: "600", color: "#00C3FF" }}>
                  {x3_average.toFixed(1)}%
                </div>
              </div>
              <div style={{ height: "40px", width: "1px", backgroundColor: "#333" }} />
              <div>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                  Long
                </div>
                <div style={{ fontSize: "16px", fontWeight: "500", color: "#00b746" }}>
                  {x1_average.toFixed(1)}%
                </div>
              </div>
              <div style={{ height: "40px", width: "1px", backgroundColor: "#333" }} />
              <div>
                <div style={{ fontSize: "11px", color: "#888", marginBottom: "4px" }}>
                  Short
                </div>
                <div style={{ fontSize: "16px", fontWeight: "500", color: "#ef403c" }}>
                  {x2_average.toFixed(1)}%
                </div>
              </div>
            </div>
          </Col>

          {/* Controls */}
          <Col xs={12} lg={6}>
            <Row>
              <Col xs={6}>
                <div style={{ padding: "0 10px" }}>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Prediction Window</span>
                    <span style={{ color: "#00C3FF", fontWeight: "600" }}>
                      {window} min
                    </span>
                  </div>
                  <Slider
                    value={window}
                    min={10}
                    max={200}
                    onChange={handleSliderChange}
                    railStyle={{ backgroundColor: "#333" }}
                    trackStyle={{ backgroundColor: "#00C3FF" }}
                    handleStyle={{
                      borderColor: "#00C3FF",
                      backgroundColor: "#00C3FF",
                    }}
                  />
                </div>
              </Col>
              <Col xs={6}>
                <div style={{ padding: "0 10px" }}>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Scale Factor</span>
                    <span style={{ color: "#00C3FF", fontWeight: "600" }}>
                      {scale}x
                    </span>
                  </div>
                  <Slider
                    value={scale}
                    min={1}
                    max={10}
                    onChange={handleScaleChange}
                    railStyle={{ backgroundColor: "#333" }}
                    trackStyle={{ backgroundColor: "#00C3FF" }}
                    handleStyle={{
                      borderColor: "#00C3FF",
                      backgroundColor: "#00C3FF",
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {/* Chart */}
      <div style={{ marginTop: "10px" }}>
        <ReactApexChart options={options} series={series} type="line" height={400} />
      </div>
    </div>
  );
};

export default PredictionChart;